/**
 * EliyaQ Audio Engine
 * ─────────────────────────────────────────────────────────────────
 * Plays queue announcements in sequence:
 *   [chime] → [digits] → [connector] → [station_name]
 *
 * Each step tries uploaded AudioAsset clips first.
 * Falls back to Web Speech API TTS if any clip is missing.
 *
 * Usage:
 *   const engine = new AudioEngine(audioAssets, settings);
 *   engine.announce(ticketNumber, stationName, language, staffName);
 */

export class AudioEngine {
  constructor(assets = [], settings = {}) {
    this.assets   = assets;   // array from GET /api/audio/
    this.settings = settings; // FacilitySettings fields
    this.queue    = [];       // announcement queue (one at a time)
    this.playing  = false;
    this.volume   = (settings.board_audio_volume ?? 80) / 100;
    this.rate     = settings.board_speech_rate ?? 1.0;
    this.gender   = settings.board_voice_gender || "default";
    this.language = settings.board_audio_language || "en";
  }

  // ── Public: queue an announcement ────────────────────────────────────────
  announce(ticketNumber, stationName, language, staffName = "") {
    this.queue.push({ ticketNumber, stationName, language: language || this.language, staffName });
    if (!this.playing) this._processNext();
  }

  // ── Internal: work through announcement queue ─────────────────────────────
  async _processNext() {
    if (this.queue.length === 0) { this.playing = false; return; }
    this.playing = true;
    const item = this.queue.shift();
    try {
      await this._playAnnouncement(item);
    } catch (e) {
      console.warn("[AudioEngine] Announcement error:", e);
    }
    // Small gap between announcements
    await this._sleep(600);
    this._processNext();
  }

  async _playAnnouncement({ ticketNumber, stationName, language, staffName }) {
    const chimeEnabled = this.settings.board_chime_enabled !== false;
    const sayStaffName = this.settings.board_announce_staff_name && staffName;

    // 1. Chime
    if (chimeEnabled) {
      await this._playClip("chime", null, language)
        .catch(() => this._tts("", language, true)); // silent if no chime clip — chime-only
    }

    // Brief pause after chime
    await this._sleep(400);

    // 2. "Ticket number" prefix via TTS (always TTS — short phrase)
    const prefix = language === "tw" ? "Ticket number" : "Ticket number";
    await this._tts(prefix, language);
    await this._sleep(150);

    // 3. Digits — e.g. "Q-047" → say "Q", then "0", "4", "7"
    await this._announceTicketNumber(ticketNumber, language);
    await this._sleep(200);

    // 4. Connector phrase: "please proceed to"
    const connectorPlayed = await this._playClip("connector", null, language).catch(() => false);
    if (!connectorPlayed) {
      const connector = language === "tw" ? "kɔ" : "please proceed to";
      await this._tts(connector, language);
    }
    await this._sleep(150);

    // 5. Station name
    const stationPlayed = await this._playClip("station_name", stationName, language).catch(() => false);
    if (!stationPlayed) {
      await this._tts(stationName, language);
    }

    // 6. Optional staff name
    if (sayStaffName) {
      await this._sleep(150);
      const withPhrase = language === "tw" ? "ɛka" : "with";
      await this._tts(`${withPhrase} ${staffName}`, language);
    }
  }

  // ── Announce ticket number digit by digit ─────────────────────────────────
  async _announceTicketNumber(ticketNumber, language) {
    // e.g. "Q-047" → chars: Q, 0, 4, 7
    const chars = ticketNumber.replace(/-/g, "").split("");
    for (const ch of chars) {
      const isDigit = /\d/.test(ch);
      if (isDigit) {
        const played = await this._playClip("number_digit", ch, language).catch(() => false);
        if (!played) await this._tts(ch, language);
      } else {
        // Letter prefix (Q, N, C etc.) — just TTS it
        await this._tts(ch, language);
      }
      await this._sleep(120);
    }
  }

  // ── Try to play an uploaded AudioAsset clip ────────────────────────────────
  _playClip(clipType, label, language) {
    return new Promise((resolve, reject) => {
      // Find matching asset
      let asset = this.assets.find(a =>
        a.clip_type === clipType &&
        a.language  === language &&
        (label === null || a.label?.toLowerCase() === label?.toLowerCase())
      );

      // Fallback: any language match
      if (!asset && label === null) {
        asset = this.assets.find(a => a.clip_type === clipType);
      }

      if (!asset?.audio_file_url) { reject(new Error("No clip")); return; }

      const audio     = new Audio(asset.audio_file_url);
      audio.volume    = this.volume;
      audio.onended   = () => resolve(true);
      audio.onerror   = () => reject(new Error("Play error"));
      audio.play().catch(reject);
    });
  }

  // ── Web Speech API TTS ────────────────────────────────────────────────────
  _tts(text, language, silent = false) {
    return new Promise((resolve) => {
      if (silent || !text?.trim()) { resolve(); return; }
      if (!window.speechSynthesis) { resolve(); return; }

      window.speechSynthesis.cancel(); // clear any ongoing speech

      const utter  = new SpeechSynthesisUtterance(text);
      utter.volume = this.volume;
      utter.rate   = this.rate;
      utter.pitch  = 1;
      utter.lang   = language === "tw" ? "ak-GH" : "en-GH";

      // Gender-aware voice selection
      const voices = window.speechSynthesis.getVoices();
      const FEMALE_PATTERNS = ["samantha","victoria","karen","moira","tessa","fiona","google uk english female","microsoft zira","allison","ava"];
      const MALE_PATTERNS   = ["daniel","alex","fred","tom","george","google uk english male","microsoft david","microsoft mark","oliver","aaron"];
      const langPrefix = language === "tw" ? "ak" : "en";
      const candidates = voices.filter(v => v.lang.toLowerCase().startsWith(langPrefix) || v.lang.toLowerCase().startsWith("en"));
      let chosenVoice = null;
      if (this.gender !== "default" && candidates.length > 0) {
        const patterns = this.gender === "female" ? FEMALE_PATTERNS : MALE_PATTERNS;
        chosenVoice = candidates.find(v => patterns.some(p => v.name.toLowerCase().includes(p)));
        if (!chosenVoice) chosenVoice = candidates[0]; // fallback
      } else {
        chosenVoice = candidates[0] || null;
      }
      if (chosenVoice) utter.voice = chosenVoice;

      utter.onend   = () => resolve();
      utter.onerror = () => resolve(); // never block on TTS error

      window.speechSynthesis.speak(utter);

      // Timeout safety — resolve after 4s no matter what
      setTimeout(resolve, 4000);
    });
  }

  _sleep(ms) {
    return new Promise(r => setTimeout(r, ms));
  }

  // Update volume at runtime (from settings slider)
  setVolume(pct) {
    this.volume = pct / 100;
  }

  // Update speech rate at runtime (from settings slider)
  setRate(rate) {
    this.rate = rate;
  }

  // Update voice gender at runtime
  setGender(gender) {
    this.gender = gender;
  }

  // Update asset library (after upload)
  setAssets(assets) {
    this.assets = assets;
  }
}
